import { useState, useCallback } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { pickBy } from 'lodash-es'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DataItem } from 'scrivito'

import { getDataItemAttributesDefaultValues } from '../read/dataItem/getDataItemDefaultValues'
import { getSchemaAttributes } from '../read/schema'
import { buildValidationSchema } from '../validation/buildValidationSchema'
import { errorToast } from '@/Components/Toast'
import { getSchemaName } from '@/utils/dataClassMap'

export const useDataItemFormHandler = (dataItem: DataItem) => {
  const { t } = useTranslation()
  const dataClassName = dataItem.dataClassName()!
  const schemaName = getSchemaName(dataClassName)
  const { schemaGroups, groupsOrder, schemaAttributes } =
    getSchemaAttributes(schemaName)
  const defaultValues = getDataItemAttributesDefaultValues(dataItem)

  // Build validation schema for visible mandatory attributes
  const validationSchemaObject = buildValidationSchema(
    schemaGroups,
    groupsOrder ?? [],
    schemaAttributes ?? {},
    t,
  )

  const methods = useForm({
    values: defaultValues,
    resolver: yupResolver(validationSchemaObject),
  })
  const { handleSubmit, reset } = methods

  const [isEditing, setIsEditing] = useState(false)
  const startEditing = () => setIsEditing(true)
  const finishEditing = () => setIsEditing(false)
  const abortEditing = () => {
    reset()
    finishEditing()
  }

  const toggleEdit = () => {
    if (!isEditing) {
      startEditing()
    }

    if (isEditing) {
      abortEditing()
    }
  }

  const onSubmit = (data: Record<string, unknown>) => {
    const definedData = pickBy(data, (e) => e !== undefined)

    dataItem
      .update(definedData)
      .then(() => reset())
      .catch((e) => errorToast(e))
      .finally(finishEditing)
  }

  const onFormSubmit = handleSubmit(onSubmit)

  const handleSave = useCallback(() => {
    onFormSubmit()
  }, [onFormSubmit])

  return {
    methods,
    onFormSubmit,
    handleSave,
    isEditing,
    setIsEditing,
    startEditing,
    finishEditing,
    toggleEdit,
    abortEditing,
  }
}
